/**
 * Main App Component with Refine Setup
 *
 * This component initializes the Refine framework with:
 * - Authentik OIDC authentication via authProvider
 * - REST API data provider with cookie-based sessions
 * - React Router for client-side navigation
 * - Ant Design UI framework with Russian localization
 * - Resources for users and transactions
 *
 * Requirements:
 * - 1.1-1.7: Authentik authentication with session cookies
 * - 2.1-2.9: Admin panel structure with routing
 * - 3.1-3.9: User list resource configuration
 * - 5.1-5.9: User details resource configuration
 *
 * Routes:
 * - / → Redirects to /users (user list)
 * - /users → User list page (placeholder)
 * - /users/show/:id → User detail page (placeholder)
 * - /login → Login redirect (handled by authProvider)
 * - * → Error page for 404s
 */

import { Refine, Authenticated } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import {
  ErrorComponent,
  useNotificationProvider,
} from "@refinedev/antd";
import routerBindings, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import { App as AntdApp, ConfigProvider } from "antd";
import ruRU from "antd/locale/ru_RU";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import {
  UserOutlined,
  TransactionOutlined,
  NotificationOutlined as CampaignOutlined,
} from "@ant-design/icons";

import { authProvider } from "./providers/authProvider";
import { dataProvider } from "./providers/dataProvider";
import { MainLayout } from "./components";
import { UserList } from "./pages/users/list";
import { UserShow } from "./pages/users/show";
import { CampaignList } from "./pages/campaigns/list";
import { CampaignCreate } from "./pages/campaigns/create";
import { CampaignEdit } from "./pages/campaigns/edit";
import { CampaignShow } from "./pages/campaigns/show";
import { Login } from "./pages/Login";

import "@refinedev/antd/dist/reset.css";

/**
 * Main application component.
 *
 * Sets up Refine framework with all necessary providers:
 * - Authentication (authProvider)
 * - Data fetching (dataProvider)
 * - Routing (react-router-dom)
 * - UI framework (Ant Design)
 * - Notifications (toast messages)
 * - Command palette (Cmd+K)
 */
function App() {
  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ConfigProvider locale={ruRU}>
          <AntdApp>
            <Refine
              authProvider={authProvider}
              dataProvider={dataProvider}
              routerProvider={routerBindings}
              notificationProvider={useNotificationProvider}
              resources={[
                {
                  name: "users",
                  list: "/users",
                  show: "/users/show/:id",
                  meta: {
                    label: "Пользователи",
                    icon: <UserOutlined />,
                  },
                },
                {
                  name: "campaigns",
                  list: "/campaigns",
                  create: "/campaigns/create",
                  edit: "/campaigns/edit/:id",
                  show: "/campaigns/show/:id",
                  meta: {
                    label: "Кампании",
                    icon: <CampaignOutlined />,
                  },
                },
                {
                  name: "transactions",
                  meta: {
                    label: "Транзакции",
                    icon: <TransactionOutlined />,
                    hide: true, // Hidden from menu, used for data fetching only
                  },
                },
              ]}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                projectId: "geektime-crm",
              }}
            >
              <Routes>
                {/* Authenticated routes with custom layout */}
                <Route
                  element={
                    <Authenticated
                      key="authenticated-layout"
                      fallback={<CatchAllNavigate to="/login" />}
                    >
                      <MainLayout>
                        <Outlet />
                      </MainLayout>
                    </Authenticated>
                  }
                >
                  {/* Root redirects to users */}
                  <Route
                    index
                    element={<NavigateToResource resource="users" />}
                  />

                  {/* User routes */}
                  <Route path="/users">
                    <Route index element={<UserList />} />
                    <Route path="show/:id" element={<UserShow />} />
                  </Route>

                  {/* Campaign routes */}
                  <Route path="/campaigns">
                    <Route index element={<CampaignList />} />
                    <Route path="create" element={<CampaignCreate />} />
                    <Route path="edit/:id" element={<CampaignEdit />} />
                    <Route path="show/:id" element={<CampaignShow />} />
                  </Route>
                </Route>

                {/* Unauthenticated routes */}
                <Route
                  element={
                    <Authenticated key="authenticated-catch-all" fallback={<Outlet />}>
                      <NavigateToResource resource="users" />
                    </Authenticated>
                  }
                >
                  <Route path="/login" element={<Login />} />
                </Route>

                {/* 404 error page */}
                <Route path="*" element={<ErrorComponent />} />
              </Routes>

              {/* Additional Refine features */}
              <RefineKbar />
              <UnsavedChangesNotifier />
              <DocumentTitleHandler />
            </Refine>
          </AntdApp>
        </ConfigProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
